import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, FolderKanban, Search, X, Eye, UserCheck, Users } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_COLORS = [
  '#7077FE', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#607D8B', '#3F51B5', '#795548'
];

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#7077FE',
    status: 'active',
    deadline: '',
    team_head_ids: [],
    member_ids: []
  });

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, [fetchProjects, fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingProject) {
        await axios.put(`${API_URL}/projects/${editingProject.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API_URL}/projects`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project created successfully');
      }
      setShowModal(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', color: '#7077FE', status: 'active', deadline: '', team_head_ids: [], member_ids: [] });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      color: project.color || '#7077FE',
      status: project.status || 'active',
      deadline: project.deadline ? project.deadline.split('T')[0] : '',
      team_head_ids: project.team_head_ids || [],
      member_ids: project.member_ids || []
    });
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? Tasks will be unlinked.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // Filter managers for team heads
  const managers = users.filter(u => ['manager', 'ceo', 'superadmin'].includes(u.role));
  
  // All active users for team members
  const teamMembers = users.filter(u => u.is_active);

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
      case 'completed': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
      case 'on_hold': return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
      case 'archived': return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9E9E9E' };
      default: return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9E9E9E' };
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="project-management">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>Project Management</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Manage your projects and track progress
          </p>
        </div>
        {canCreate('projects') && (
          <button
            onClick={() => { setEditingProject(null); setFormData({ name: '', description: '', color: '#7077FE', status: 'active', deadline: '', team_head_ids: [], member_ids: [] }); setShowModal(true); }}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
            data-testid="create-project-btn"
          >
            <Plus size={18} />
            Create Project
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '24px', position: 'relative', maxWidth: '400px' }}>
        <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input
          type="text"
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 12px 12px 44px',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}
          data-testid="project-search"
        />
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
          <FolderKanban size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No Projects Found</h3>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Create your first project to get started</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          {filteredProjects.map(project => {
            const statusStyle = getStatusStyle(project.status);
            const stats = project.task_stats || { total: 0, pending: 0, in_progress: 0, completed: 0 };
            const progressPercent = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;

            return (
              <div
                key={project.id}
                className="card"
                style={{ padding: '0', overflow: 'hidden' }}
                data-testid={`project-card-${project.id}`}
              >
                {/* Color Bar */}
                <div style={{ height: '6px', background: project.color || '#7077FE' }} />

                <div style={{ padding: '20px' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {project.name}
                      </h3>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: statusStyle.bg,
                        color: statusStyle.color,
                        textTransform: 'capitalize'
                      }}>
                        {project.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => navigate(`/dashboard/projects/${project.id}`)}
                        style={{ padding: '8px', background: 'rgba(112, 119, 254, 0.1)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#7077FE' }}
                        data-testid={`view-project-${project.id}`}
                        title="View project details"
                      >
                        <Eye size={16} />
                      </button>
                      {canEdit('projects') && (
                        <button
                          onClick={() => handleEdit(project)}
                          style={{ padding: '8px', background: 'var(--bg-secondary)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                          data-testid={`edit-project-${project.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      {canDelete('projects') && (
                        <button
                          onClick={() => handleDelete(project.id)}
                          style={{ padding: '8px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#EF4444' }}
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {project.description && (
                    <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {(() => {
                        // Strip HTML tags for card preview
                        const strippedDesc = project.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
                        return strippedDesc.length > 100 ? `${strippedDesc.substring(0, 100)}...` : strippedDesc;
                      })()}
                    </p>
                  )}

                  {/* Progress Bar */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Progress</span>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{Math.round(progressPercent)}%</span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--bg-secondary)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${progressPercent}%`, background: project.color || '#7077FE', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                    </div>
                  </div>

                  {/* Task Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {[
                      { label: 'Total', value: stats.total, color: 'var(--text-primary)' },
                      { label: 'Pending', value: stats.pending, color: '#F59E0B' },
                      { label: 'Active', value: stats.in_progress, color: '#3B82F6' },
                      { label: 'Done', value: stats.completed, color: '#10B981' }
                    ].map(stat => (
                      <div key={stat.label} style={{ textAlign: 'center', padding: '8px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} data-testid="project-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {editingProject ? 'Edit Project' : 'Create Project'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter project name"
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="project-name-input"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Description</label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Enter project description..."
                  minHeight="120px"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                  <UserCheck size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Team Heads (Managers)
                </label>
                <select
                  multiple
                  value={formData.team_head_ids}
                  onChange={(e) => setFormData({ ...formData, team_head_ids: Array.from(e.target.selectedOptions, opt => opt.value) })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', minHeight: '100px' }}
                  data-testid="project-team-heads-select"
                >
                  {managers.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name} ({user.role})</option>
                  ))}
                </select>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>Hold Ctrl/Cmd to select multiple</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                  <Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Team Members
                </label>
                <select
                  multiple
                  value={formData.member_ids}
                  onChange={(e) => setFormData({ ...formData, member_ids: Array.from(e.target.selectedOptions, opt => opt.value) })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', minHeight: '100px' }}
                  data-testid="project-members-select"
                >
                  {teamMembers.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name} ({user.role})</option>
                  ))}
                </select>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>Hold Ctrl/Cmd to select multiple</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Project Color</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {PROJECT_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: color,
                        border: formData.color === color ? '3px solid var(--text-primary)' : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Deadline</label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="project-deadline-input"
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="project-status-select"
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: 'var(--bg-secondary)', paddingTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px' }}
                  data-testid="project-submit-btn"
                >
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
